"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Compass, Phone } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Work() {
  const shouldReduceMotion = useReducedMotion();
  const initialMotion = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const animateMotion = {
    opacity: 1,
    y: 0
  };
  const motionTransition = shouldReduceMotion ? {
    duration: 0
  } : {
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="work-with-us" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 border-t border-border overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial={initialMotion} whileInView={animateMotion} viewport={{
        once: true,
        amount: 0.3
      }} transition={motionTransition} className="relative rounded-2xl bg-card/90 backdrop-blur-md border border-border shadow-md overflow-hidden">
          {/* Architectural Copper Hairline Accent */}
          <div className="h-[3px] w-full bg-primary" />

          <div className="px-6 py-10 sm:px-12 sm:py-14 lg:py-16 text-center">
            {/* Architectural Eyebrow Badge */}
            <div className="flex justify-center mb-6">
              <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold tracking-wider uppercase bg-secondary text-secondary-foreground border border-border rounded-full">
                Start Your Project
              </Badge>
            </div>

            {/* Fraunces Display Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight max-w-3xl mx-auto leading-tight">
              Ready to discuss your Decatur home renovation?
            </h2>

            {/* Concise Supporting Description */}
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Tell us about your space and timeline. We will walk through your home, explore design possibilities, and provide a clear, itemized project plan.
            </p>

            {/* Action Group */}
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium px-7 py-3.5 rounded-lg bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-[color,background-color,border-color,box-shadow]">
                <Link to="#" className="inline-flex items-center justify-center gap-2">
                  <span>Request an estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-medium px-7 py-3.5 rounded-lg bg-secondary text-secondary-foreground border border-border hover:bg-muted transition-[color,background-color,border-color,box-shadow]">
                <Link to="#" className="inline-flex items-center justify-center gap-2">
                  <span>View recent projects</span>
                </Link>
              </Button>
            </div>

            {/* Quick Contact & Process Features Strip */}
            <div className="mt-12 pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
              <div className="flex items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary border border-border">
                  <Compass className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Local consultation</h3>
                  <p className="text-xs text-muted-foreground">On-site review across Decatur</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary border border-border">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Clear timeline</h3>
                  <p className="text-xs text-muted-foreground">Realistic scheduling and planning</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary border border-border">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Direct conversation</h3>
                  <p className="text-xs text-muted-foreground">Call (470) 847-9076</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}